// Platform AI clients (universal-LLM Phase 3 hygiene). Self-registered (DCR)
// and platform-level OAuth clients — Claude/ChatGPT-style assistants — belong
// to no tenant, so this console page is the ONLY place they are visible.
// Operators see the live consent footprint, disable a client (cuts every
// refresh-token family), and run the orphan janitor on demand.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useState } from 'react';
import { Button, Popconfirm, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { ClearOutlined, ReloadOutlined, RobotOutlined } from '@ant-design/icons';
import { disableAiClient, enableAiClient, listAiClients, purgeAiClientOrphans, type PlatformAiClient } from '../../services/sandboxService';

const AiClientsPage: React.FC = () => {
  const [rows, setRows] = useState<PlatformAiClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await listAiClients());
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load AI clients.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);

  const toggle = async (row: PlatformAiClient) => {
    setBusyId(row.id);
    try {
      if (row.status === 'active') {
        const res = await disableAiClient(row.id);
        message.success(`${row.name} disabled — ${res.revokedTokens} token(s) revoked.`);
      } else {
        await enableAiClient(row.id);
        message.success(`${row.name} re-enabled. Users must consent again to reconnect.`);
      }
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not update the client.');
    } finally {
      setBusyId(null);
    }
  };

  const purge = async () => {
    try {
      const res = await purgeAiClientOrphans();
      message.success(`Janitor: purged ${res.purged} orphaned client(s), kept ${res.kept}.`);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Purge failed.');
    }
  };

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          <RobotOutlined /> AI clients
        </Typography.Title>
        <Space>
          <Tooltip title="Delete self-registered clients older than 7 days that never completed a consent (no codes, no tokens, never used). Linked clients are never touched.">
            <Popconfirm title="Purge orphaned self-registered clients?" onConfirm={purge}>
              <Button icon={<ClearOutlined />}>Purge orphans</Button>
            </Popconfirm>
          </Tooltip>
          <Button icon={<ReloadOutlined />} onClick={load} />
        </Space>
      </Space>
      <Typography.Paragraph type="secondary">
        Platform-level and dynamically-registered (RFC 7591) OAuth clients — AI assistants like Claude
        connect through these. They belong to no tenant, so this is the only place they are visible.
        Disabling one revokes every live connection at its next token refresh.
      </Typography.Paragraph>
      <Table<PlatformAiClient>
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={{ pageSize: 20 }}
        columns={[
          { title: 'Name', dataIndex: 'name', render: (v, r) => (
            <Space direction="vertical" size={0}>
              <span>{v}</span>
              <Typography.Text type="secondary" copyable style={{ fontSize: 12 }}>{r.clientId}</Typography.Text>
            </Space>
          ) },
          { title: 'Source', dataIndex: 'source', render: (v) => v === 'dcr'
            ? <Tag color="orange">self-registered</Tag> : <Tag>portal</Tag> },
          { title: 'Kind', dataIndex: 'kind', render: (v) => <Tag color={v === 'agent' ? 'geekblue' : undefined}>{v}</Tag> },
          { title: 'Auth', dataIndex: 'confidential', render: (v) => (v ? 'confidential' : 'public (PKCE)') },
          { title: 'Connections', dataIndex: 'activeConnections', sorter: (a, b) => a.activeConnections - b.activeConnections,
            render: (v, r) => v ? `${v} (${r.tenantsLinked} tenant${r.tenantsLinked === 1 ? '' : 's'})` : '—' },
          { title: 'Last used', dataIndex: 'lastUsedAt', render: (v) => (v ? fmtDateTime(v) : 'never') },
          { title: 'Status', dataIndex: 'status', render: (v) => (v === 'active'
            ? <Tag color="green">active</Tag> : <Tag color="red">disabled</Tag>) },
          { title: '', key: 'actions', render: (_, r) => (
            <Popconfirm
              title={r.status === 'active'
                ? `Disable ${r.name}? All ${r.activeConnections} connection(s) will be revoked.`
                : `Re-enable ${r.name}?`}
              onConfirm={() => toggle(r)}
            >
              <Button size="small" danger={r.status === 'active'} loading={busyId === r.id}>
                {r.status === 'active' ? 'Disable' : 'Enable'}
              </Button>
            </Popconfirm>
          ) },
        ]}
      />
    </div>
  );
};

export { AiClientsPage };
export default AiClientsPage;
